import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  SelectInput,
  DateInput,
  BooleanInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const InspectionToolbar = () => (
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0',
    }}
  >
    <SaveButton />
  </Toolbar>
);

interface InspectionDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const InspectionDialog: React.FC<InspectionDialogProps> = ({
  open,
  onClose,
  equipmentId,
  recordId,
  isEdit = false,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [record, setRecord] = useState(null);

  const inspectionTypes = [
    { id: 'ANNUAL', name: 'Annual Inspection' },
    { id: 'DAILY', name: 'Daily Inspection' },
    { id: 'PRE_TRIP', name: 'Pre-Trip Inspection' },
    { id: 'POST_TRIP', name: 'Post-Trip Inspection' },
    { id: 'DOT', name: 'DOT Inspection' },
    { id: 'SAFETY', name: 'Safety Inspection' },
    { id: 'EMISSIONS', name: 'Emissions Inspection' },
    { id: 'OTHER', name: 'Other' },
  ];

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      true;
      dataProvider
        .getOne('equipment-inspections', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch(error => {
          console.error('Error loading inspection record:', error);
          notify('Error loading inspection record', { type: 'error' });
        })
        .finally(() => {
          false;
        });
    } else if (!isEdit) {
      setRecord(null);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    true;
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };

      if (isEdit && recordId) {
        await dataProvider.update('equipment-inspections', {
          id: recordId,
          data: formattedData,
        });
        notify('Inspection record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-inspections', {
          data: formattedData,
        });
        notify('Inspection record created successfully', { type: 'success' });
      }

      refresh();
      onClose();
    } catch (error) {
      console.error('Inspection submission error:', error);
      notify(
        `Error ${isEdit ? 'updating' : 'creating'} inspection record: ${
          error.message || 'Unknown error'
        }`,
        { type: 'error' }
      );
    } finally {
      false;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit
          ? translate('resources.equipment_inspections.edit.title')
          : translate('resources.equipment_inspections.create.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm
          onSubmit={handleSubmit}
          record={
            record || {
              inspectionDate: new Date().toISOString().split('T')[0],
            }
          }
          toolbar={<InspectionToolbar />}
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <DateInput
                source="inspectionDate"
                validate={[required()]}
                fullWidth
                label="Inspection Date"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="inspectionType"
                choices={inspectionTypes}
                validate={[required()]}
                fullWidth
                label="Inspection Type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput source="inspector" fullWidth label="Inspector" />
            </Grid>
            <Grid item xs={12} md={6}>
              <BooleanInput source="passed" label="Passed" />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateInput source="nextDueDate" fullWidth label="Next Due Date" />
            </Grid>
            <Grid item xs={12}>
              <TextInput source="notes" fullWidth multiline rows={3} label="Notes" />
            </Grid>
          </Grid>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
